import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Terminal, ShieldCheck, Zap, Users, MessageSquare, Sparkles, ArrowRight,
  Send, Mail, ChevronRight, Crown, Bot, Wrench, Globe, BadgeCheck, Swords,
} from 'lucide-react';
import { useSiteConfig } from '../contexts/SiteConfigContext';

// Icon lookup for admin-editable crew members (config stores string keys).
const ICONS = { wrench: Wrench, zap: Zap, bot: Bot, users: Users, message: MessageSquare, shield: ShieldCheck, sparkles: Sparkles, crown: Crown, globe: Globe, terminal: Terminal };

// Hard-coded fallback used only if `site_config.team_page` is missing (edge
// case — auto-migration inserts the default on first read).
const OWNER_FALLBACK = {
  callsign: 'ERR0R-CIPHER',
  name: 'The Founder',
  legal_hint: 'anonymous by design',
  tagline: 'building the underground the way it should have been built.',
  quote: 'we don\'t just recover accounts. we recover trust. every case is personal.',
  roles: [
    'Founder & Lead Analyst',
    'Ethical Hacker · OSCP',
    'Incident Response Coach',
    'Security Educator',
    'AI + Defense Architect',
  ],
  stats: [
    { label: 'YEARS · CYBER OPS', value: '9+',     color: '#00ff9d' },
    { label: 'CASES COACHED',     value: '3,200+', color: '#ff2d92' },
    { label: 'COUNTRIES',         value: '42',     color: '#4de0ff' },
    { label: 'AI TOOLS SHIPPED',  value: '17',     color: '#ffd34d' },
  ],
  skills: [
    'incident response coaching', 'social engineering defence', 'osint',
    'cybersecurity education', 'ai-driven diagnostics', 'authorised audits',
    'wallet ops', 'python · fastapi · react', 'threat modelling',
  ],
};

const TEAM_FALLBACK = [
  { id: 'm_viper',  callsign: 'ERR0R-VIPER',   role: 'Consultation Lead',    tagline: 'incident triage · 7d/wk',            color: '#ff2d92', icon: 'wrench',  active: true, sort: 1 },
  { id: 'm_krait',  callsign: 'ERR0R-KRAIT',   role: 'Defensive Recon',      tagline: 'OSINT · asset discovery · CTF',      color: '#4de0ff', icon: 'search',  active: true, sort: 2 },
  { id: 'm_mamba',  callsign: 'ERR0R-MAMBA',   role: 'AI Research',          tagline: 'trains the security-suite brains',   color: '#c084fc', icon: 'bot',     active: true, sort: 3 },
  { id: 'm_gato',   callsign: 'ERR0R-GATO',    role: 'Community Manager',    tagline: 'runs the ops chat 04:00-24:00',      color: '#ffd34d', icon: 'users',   active: true, sort: 4 },
  { id: 'm_valk',   callsign: 'ERR0R-VALK',    role: 'Education · Books',    tagline: 'writes the learner playbooks',       color: '#f472b6', icon: 'message', active: true, sort: 5 },
  { id: 'm_shield', callsign: 'ERR0R-SHIELD',  role: 'Ethics & Compliance',  tagline: 'keeps every op above the line',      color: '#00ff9d', icon: 'shield',  active: true, sort: 6 },
];

const TeamPage = () => {
  const { config } = useSiteConfig();
  const SITE = config.site;
  const teamCfg = config.team_page || {};
  const OWNER = { ...OWNER_FALLBACK, ...(teamCfg.owner || {}) };
  const TEAM = (teamCfg.members && teamCfg.members.length ? teamCfg.members : TEAM_FALLBACK)
    .filter(m => m && (m.active === undefined || m.active))
    .slice()
    .sort((a, b) => (a.sort || 0) - (b.sort || 0));

  const socials = useMemo(() => ([
    { kind: 'telegram', label: 'DM ON TELEGRAM', href: OWNER.telegram_url || SITE.telegram || 'https://t.me/', color: '#4de0ff', Icon: Send },
    { kind: 'email',    label: 'EMAIL FOUNDER',   href: `mailto:${OWNER.email || SITE.email || 'hello@errorhacker.site'}`, color: '#00ff9d', Icon: Mail },
  ]), [OWNER.telegram_url, OWNER.email, SITE.telegram, SITE.email]);

  // Rotating owner role
  const [roleIdx, setRoleIdx] = useState(0);
  useEffect(() => {
    if (!OWNER.roles || OWNER.roles.length <= 1) return;
    const t = setInterval(() => setRoleIdx(i => (i + 1) % OWNER.roles.length), 2600);
    return () => clearInterval(t);
  }, [OWNER.roles]);

  // Magnetic tilt for team tiles — small transform based on mouse position
  const magneticRefs = useRef([]);
  useEffect(() => {
    const handlers = [];
    magneticRefs.current.forEach((el) => {
      if (!el) return;
      const onMove = (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) / r.width;
        const y = (e.clientY - r.top - r.height / 2) / r.height;
        el.style.transform = `translateY(-4px) rotateX(${-y * 4}deg) rotateY(${x * 6}deg)`;
      };
      const onLeave = () => { el.style.transform = ''; };
      el.addEventListener('mousemove', onMove);
      el.addEventListener('mouseleave', onLeave);
      handlers.push([el, onMove, onLeave]);
    });
    return () => handlers.forEach(([el, m, l]) => {
      el.removeEventListener('mousemove', m);
      el.removeEventListener('mouseleave', l);
    });
  }, []);

  const initials = useMemo(() => OWNER.callsign.split('-').map(s => s[0]).join('').slice(0, 2), [OWNER.callsign]);

  // Resolve avatar URLs (upload paths come as `/api/uploads/<id>`; also allow
  // absolute http(s):// links or data: URIs pasted from other sources).
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  const resolveAsset = (u) => {
    if (!u) return '';
    if (u.startsWith('http') || u.startsWith('data:')) return u;
    return `${backendUrl}${u.startsWith('/') ? '' : '/'}${u}`;
  };
  const ownerPhotoSrc = resolveAsset(OWNER.avatar_url);

  return (
    <div className="team-page relative pb-20" data-testid="team-page">
      {/* ============ CINEMATIC HERO ============ */}
      <section className="team-hero relative overflow-hidden">
        {/* Ambient neon blobs */}
        <div aria-hidden className="blob b1" />
        <div aria-hidden className="blob b2" />
        <div aria-hidden className="blob b3" />
        {/* Grain overlay */}
        <div aria-hidden className="grain" />
        {/* Scanlines */}
        <div aria-hidden className="scanlines" />
        {/* Grid dots */}
        <div aria-hidden className="grid-dots" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-16 sm:pb-24">
          {/* Breadcrumb */}
          <Link to="/info" className="inline-flex items-center gap-1.5 eh-mono text-[10px] tracking-widest opacity-60 hover:opacity-100 hover:text-[var(--eh-green)] mb-6 sm:mb-10">
            <ChevronRight size={11} className="rotate-180" /> BACK TO INFO
          </Link>

          <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] items-center gap-8 sm:gap-10">
            {/* AVATAR CARD */}
            <div className="relative mx-auto lg:mx-0 avatar-holder" data-testid="team-owner-avatar">
              <div className="ring-pulse" aria-hidden />
              <div className="ring-arc" aria-hidden />
              <div className="avatar-inner">
                <div className="corner tl" aria-hidden />
                <div className="corner tr" aria-hidden />
                <div className="corner bl" aria-hidden />
                <div className="corner br" aria-hidden />
                <div className="avatar-face">
                  {ownerPhotoSrc ? (
                    <img
                      src={ownerPhotoSrc}
                      alt={OWNER.callsign}
                      className="avatar-photo"
                      data-testid="team-owner-photo"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                    />
                  ) : (
                    <>
                      <Terminal size={44} strokeWidth={1.5} />
                      <div className="callsign">{initials}</div>
                    </>
                  )}
                </div>
                <div className="status-dot" aria-hidden />
                {/* OP·01 badge — founder is always rank 1 */}
                <div className="owner-rank-badge eh-mono" data-testid="team-owner-rank">OP·01</div>
                {ownerPhotoSrc && (
                  <div className="owner-verified" title="Verified · founder" aria-hidden>
                    <BadgeCheck size={16} strokeWidth={2.4} />
                  </div>
                )}
              </div>
              <div className="under-tag eh-mono text-[10px] tracking-[0.3em] mt-4 opacity-70">
                // <span style={{ color: 'var(--eh-green)' }}>LIVE</span> · IN OPS
              </div>
            </div>

            {/* NAME / TITLE STACK */}
            <div className="min-w-0">
              <div className="eh-kicker mb-4">// FOUNDER · CEO · OPERATIVE-01</div>

              <h1 className="owner-name font-black leading-[0.94]" data-testid="team-owner-name">
                {OWNER.callsign.split('').map((c, i) => (
                  <span key={i} className="letter" style={{ animationDelay: `${i * 45}ms` }}>{c === '-' ? <span className="dash">·</span> : c}</span>
                ))}
              </h1>

              <div className="mt-3 flex items-center gap-3">
                <div className="role-chip" data-testid="team-owner-role">
                  <Crown size={12} className="text-[#ffd34d] shrink-0" />
                  <span key={roleIdx} className="role-text">{OWNER.roles?.[roleIdx] || OWNER.roles?.[0] || ''}</span>
                </div>
                <span className="eh-mono text-[10px] opacity-50 tracking-widest">{OWNER.legal_hint || OWNER.legalHint || ''}</span>
              </div>

              <p className="mt-5 max-w-xl text-sm sm:text-base opacity-80 leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
                {OWNER.tagline}
              </p>

              {/* STAT ROW */}
              <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="team-owner-stats">
                {(OWNER.stats || []).map(s => (
                  <div key={s.label} className="stat-cell" style={{ '--c': s.color }}>
                    <div className="stat-value" style={{ color: s.color }}>{s.value}</div>
                    <div className="stat-label eh-mono">{s.label}</div>
                  </div>
                ))}
              </div>

              {/* CTAs */}
              <div className="mt-7 flex flex-wrap items-center gap-3">
                {socials.map(s => {
                  const Icon = s.Icon;
                  return (
                    <a
                      key={s.kind}
                      href={s.href}
                      target={s.kind === 'telegram' ? '_blank' : undefined}
                      rel={s.kind === 'telegram' ? 'noreferrer' : undefined}
                      data-testid={`team-owner-${s.kind}`}
                      className="cta-btn"
                      style={{ '--c': s.color }}
                    >
                      <Icon size={14} /> {s.label}
                    </a>
                  );
                })}
                <Link
                  to="/advisory"
                  className="cta-btn ghost"
                  style={{ '--c': '#ff2d92' }}
                  data-testid="team-owner-work"
                >
                  <Sparkles size={14} /> BOOK A CONSULTATION
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ SIGNATURE QUOTE ============ */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 -mt-6 sm:-mt-8 mb-16 sm:mb-24 relative z-10">
        <blockquote className="quote-card" data-testid="team-owner-quote">
          <span className="quote-mark">&ldquo;</span>
          <p className="text-lg sm:text-2xl font-medium leading-relaxed italic" style={{ fontFamily: 'Inter,sans-serif' }}>
            {OWNER.quote}
          </p>
          <div className="quote-sig eh-mono text-[10px] tracking-[0.3em] mt-4">
            — {OWNER.callsign} · <span style={{ color: 'var(--eh-green)' }}>signed_encrypted</span>
          </div>
        </blockquote>
      </section>

      {/* ============ TEAM GRID ============ */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mb-16 sm:mb-24">
        <div className="text-center mb-10">
          <div className="eh-kicker justify-center mb-3">// THE_CREW</div>
          <h2 className="eh-display text-3xl sm:text-5xl font-black leading-tight">
            <span>The operators behind </span><span className="eh-neon">every op</span>
          </h2>
          <p className="mt-3 opacity-70 text-sm max-w-xl mx-auto leading-relaxed" style={{ fontFamily: 'Inter,sans-serif' }}>
            Handpicked. Vetted. Encrypted. Everyone on this list has shipped in production for at least 3 years.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="team-crew-grid">
          {TEAM.map((m, i) => (
            <CrewTile
              key={m.id || m.callsign}
              m={m}
              rank={i + 2}
              tileRef={(el) => (magneticRefs.current[i] = el)}
              resolveAsset={resolveAsset}
              Icon={ICONS[m.icon] || Wrench}
            />
          ))}
        </div>
      </section>

      {/* ============ SKILLS MARQUEE ============ */}
      <section className="skills-strip" data-testid="team-skills">
        <div className="skills-track">
          {[...(OWNER.skills || []), ...(OWNER.skills || [])].map((s, i) => (
            <span key={i} className="skill-pill">
              <span className="dot" /> {s}
            </span>
          ))}
        </div>
      </section>

      {/* ============ CONTACT PANEL ============ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 mt-16 sm:mt-24">
        <div className="contact-card">
          <span className="prism-bar" aria-hidden />
          <div className="eh-kicker justify-center mb-3">// REACH_OUT</div>
          <h2 className="eh-display text-2xl sm:text-4xl font-black mb-3 text-center">
            NEED A <span className="eh-neon">CUSTOM OP?</span>
          </h2>
          <p className="opacity-70 text-sm mb-6 text-center" style={{ fontFamily: 'Inter,sans-serif' }}>
            The founder personally reviews every request within 12 hours. No middleman, no phone-tree, no ticket queue.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={SITE.telegram} target="_blank" rel="noreferrer" className="eh-btn-primary" data-testid="team-contact-telegram">
              <Send size={16} /> DM ON TELEGRAM
            </a>
            <a href={`mailto:${SITE.email}`} className="eh-btn-ghost" data-testid="team-contact-email">
              <Mail size={14} /> EMAIL DIRECT
            </a>
            <Link to="/tools" className="eh-btn-ghost" data-testid="team-contact-services">
              <Globe size={14} /> AI SECURITY TOOLS <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* ================= INLINE STYLES (SCOPED) ================= */}
      <style>{`
        .team-page { --owner-c: var(--eh-green); }

        /* HERO */
        .team-hero {
          position: relative;
          background: radial-gradient(ellipse at 30% -10%, rgba(0,255,157,.10), transparent 60%),
                      radial-gradient(ellipse at 100% 30%, rgba(255,45,146,.09), transparent 55%),
                      radial-gradient(ellipse at 20% 100%, rgba(77,224,255,.08), transparent 55%);
          isolation: isolate;
        }
        .team-hero::after {
          content: '';
          position: absolute; left: 0; right: 0; bottom: 0; height: 1px;
          background: linear-gradient(90deg, transparent, rgba(0,255,157,.4), transparent);
        }
        .blob { position: absolute; border-radius: 9999px; filter: blur(80px); opacity: .35; z-index: -1; animation: float 14s ease-in-out infinite; }
        .blob.b1 { width: 340px; height: 340px; background: #00ff9d; top: -80px; left: -80px; }
        .blob.b2 { width: 280px; height: 280px; background: #ff2d92; top: 40%; right: -60px; animation-delay: -4s; }
        .blob.b3 { width: 260px; height: 260px; background: #4de0ff; bottom: -60px; left: 40%; animation-delay: -8s; }
        @keyframes float {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(24px, -18px) scale(1.06); }
        }
        .grain {
          position: absolute; inset: 0; pointer-events: none; opacity: .08; z-index: -1;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
        }
        .scanlines {
          position: absolute; inset: 0; pointer-events: none; z-index: -1; opacity: .04;
          background-image: repeating-linear-gradient(0deg, transparent 0, transparent 2px, #00ff9d 3px, transparent 4px);
        }
        .grid-dots {
          position: absolute; inset: 0; pointer-events: none; z-index: -1; opacity: .08;
          background-image: radial-gradient(rgba(255,255,255,.35) 1px, transparent 1px);
          background-size: 22px 22px;
          mask-image: radial-gradient(ellipse at center, black 45%, transparent 80%);
        }

        /* AVATAR */
        .avatar-holder { position: relative; width: 260px; height: 260px; }
        @media (max-width: 640px) { .avatar-holder { width: 220px; height: 220px; } }
        .ring-pulse {
          position: absolute; inset: -14px; border-radius: 9999px;
          border: 1.5px solid rgba(0,255,157,.35);
          animation: ringPulse 3s ease-in-out infinite;
        }
        @keyframes ringPulse {
          0%, 100% { transform: scale(1); opacity: .35; box-shadow: 0 0 30px rgba(0,255,157,.35); }
          50% { transform: scale(1.06); opacity: .9; box-shadow: 0 0 60px rgba(0,255,157,.55); }
        }
        .ring-arc {
          position: absolute; inset: -30px; border-radius: 9999px;
          background: conic-gradient(from 0deg, transparent 0%, #00ff9d 8%, transparent 22%, transparent 50%, #ff2d92 55%, transparent 70%, transparent 100%);
          animation: spin 8s linear infinite; opacity: .55;
          mask: radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 3px));
          -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 3px));
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .avatar-inner {
          position: relative; width: 100%; height: 100%; border-radius: 9999px;
          background: radial-gradient(circle at 40% 30%, rgba(0,255,157,.14), rgba(0,0,0,.85));
          border: 2px solid rgba(0,255,157,.55);
          display: grid; place-items: center; overflow: hidden;
          box-shadow: inset 0 0 40px rgba(0,255,157,.15), 0 0 40px rgba(0,255,157,.2);
        }
        .corner { position: absolute; width: 22px; height: 22px; border: 2px solid var(--eh-green); }
        .corner.tl { top: 12px; left: 12px; border-right: none; border-bottom: none; }
        .corner.tr { top: 12px; right: 12px; border-left: none; border-bottom: none; }
        .corner.bl { bottom: 12px; left: 12px; border-right: none; border-top: none; }
        .corner.br { bottom: 12px; right: 12px; border-left: none; border-top: none; }
        .avatar-face {
          display: flex; flex-direction: column; align-items: center; gap: 8px;
          color: var(--eh-green);
          width: 100%; height: 100%; justify-content: center;
        }
        .avatar-photo {
          width: 100%; height: 100%; object-fit: cover; border-radius: 9999px;
          filter: contrast(1.05) saturate(1.05);
        }
        .callsign {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-weight: 900; font-size: 44px; letter-spacing: 6px; line-height: 1;
          background: linear-gradient(180deg, #00ff9d, #4de0ff);
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
        }
        .status-dot {
          position: absolute; bottom: 26px; right: 26px; width: 12px; height: 12px; border-radius: 9999px;
          background: #00ff9d; box-shadow: 0 0 12px #00ff9d;
          animation: blip 1.4s ease-in-out infinite;
        }
        @keyframes blip { 0%,100% { opacity: 1; transform: scale(1); } 50% { opacity: .6; transform: scale(1.3); } }
        .under-tag { text-align: center; }
        .owner-rank-badge {
          position: absolute; top: 12px; left: 50%; transform: translateX(-50%);
          padding: 4px 12px; border-radius: 999px;
          font-size: 10px; letter-spacing: 0.3em; font-weight: 800;
          color: #ffd34d;
          background: linear-gradient(160deg, rgba(255,211,77,.22), rgba(0,0,0,.5));
          border: 1px solid rgba(255,211,77,.55);
          box-shadow: 0 0 22px rgba(255,211,77,.28), inset 0 0 12px rgba(255,211,77,.1);
          z-index: 5; white-space: nowrap;
        }
        .owner-verified {
          position: absolute; right: 22px; bottom: 22px;
          width: 32px; height: 32px; border-radius: 9999px;
          display: grid; place-items: center;
          color: #fff;
          background: radial-gradient(circle at 30% 30%, #4de0ff, #06a3d6);
          box-shadow: 0 0 0 3px #05060a, 0 6px 18px -2px rgba(77,224,255,.7);
          z-index: 5;
        }

        /* NAME REVEAL */
        .owner-name {
          font-family: 'Cinzel', 'Playfair Display', 'JetBrains Mono', serif;
          font-size: clamp(2.4rem, 8.5vw, 5.6rem);
          letter-spacing: -0.02em;
        }
        .owner-name .letter {
          display: inline-block; opacity: 0; transform: translateY(24px) rotate(-4deg);
          animation: nameIn .8s cubic-bezier(.2,.9,.3,1) forwards;
          background: linear-gradient(180deg, #ffffff 0%, #d5d5d5 55%, #7a7a7a 100%);
          -webkit-background-clip: text; background-clip: text;
          -webkit-text-fill-color: transparent; color: transparent;
        }
        .owner-name .dash {
          background: none; -webkit-background-clip: unset; background-clip: unset;
          -webkit-text-fill-color: var(--eh-green); color: var(--eh-green);
          padding: 0 6px;
        }
        @keyframes nameIn {
          to { opacity: 1; transform: translateY(0) rotate(0); }
        }

        /* ROLE CHIP */
        .role-chip {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 6px 12px; border-radius: 999px;
          background: rgba(255,211,77,.08); border: 1px solid rgba(255,211,77,.35);
          font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.15em;
          color: #ffd34d;
        }
        .role-text {
          display: inline-block; animation: roleFlip .5s cubic-bezier(.2,.9,.3,1);
        }
        @keyframes roleFlip {
          from { opacity: 0; transform: translateY(6px); filter: blur(3px); }
          to   { opacity: 1; transform: translateY(0);    filter: blur(0); }
        }

        /* STATS */
        .stat-cell {
          padding: 12px 14px; border-radius: 10px;
          background: linear-gradient(160deg, color-mix(in srgb, var(--c) 10%, transparent), transparent 70%);
          border: 1px solid color-mix(in srgb, var(--c) 35%, transparent);
          position: relative; overflow: hidden;
          transition: transform .25s ease, border-color .2s ease;
        }
        .stat-cell:hover { transform: translateY(-2px); border-color: var(--c); }
        .stat-value { font-family: 'Cinzel', serif; font-weight: 900; font-size: clamp(1.5rem, 3vw, 2rem); line-height: 1; }
        .stat-label { font-size: 9px; letter-spacing: 0.25em; opacity: .7; margin-top: 6px; }

        /* CTAs */
        .cta-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 16px; border-radius: 8px;
          background: var(--c); color: #000;
          font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.18em; font-weight: 700;
          transition: transform .2s ease, filter .2s ease;
        }
        .cta-btn:hover { transform: translateY(-2px); filter: brightness(1.1); }
        .cta-btn.ghost {
          background: transparent; color: var(--c);
          border: 1px solid color-mix(in srgb, var(--c) 45%, transparent);
        }
        .cta-btn.ghost:hover { border-color: var(--c); background: color-mix(in srgb, var(--c) 8%, transparent); }

        /* QUOTE CARD */
        .quote-card {
          position: relative; padding: 32px 28px;
          border-radius: 20px; border: 1px solid rgba(0,255,157,.25);
          background: linear-gradient(160deg, rgba(0,255,157,.05), rgba(255,45,146,.03) 60%, transparent);
          box-shadow: 0 20px 60px -20px rgba(0,255,157,.25);
          text-align: center;
          overflow: hidden;
        }
        .quote-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, transparent, #00ff9d, #ff2d92, #4de0ff, transparent);
        }
        .quote-mark {
          display: block; font-family: 'Cinzel', serif; font-size: 90px; line-height: 0.4;
          color: var(--eh-green); opacity: .5; margin-bottom: 8px;
        }
        .quote-sig { color: rgba(255,255,255,.65); }

        /* CREW TILES · Iter-38 · ROYAL LOOK
           Center-aligned portrait card with gold-tinged frame, ornate
           corners, and a per-member accent color for the base ring. */
        .crew-tile {
          position: relative;
          padding: 30px 22px 26px;
          border-radius: 18px;
          background:
            radial-gradient(ellipse at 50% 0%, color-mix(in srgb, var(--c) 14%, transparent), transparent 65%),
            linear-gradient(160deg, rgba(255,255,255,.03), rgba(0,0,0,.35));
          border: 1px solid color-mix(in srgb, var(--c) 30%, transparent);
          overflow: hidden;
          transform-style: preserve-3d;
          transition: transform .35s cubic-bezier(.2,.9,.3,1), border-color .25s ease, box-shadow .35s ease;
          cursor: default;
          text-align: center;
          min-height: 340px;
        }
        @media (max-width: 640px) {
          .crew-tile { padding: 26px 18px 22px; min-height: 320px; }
        }
        .crew-tile::before {
          /* Subtle gold pinstripe on the entire card border */
          content: ''; position: absolute; inset: 0; border-radius: inherit;
          padding: 1px; pointer-events: none;
          background: linear-gradient(160deg, rgba(255,211,77,.35), transparent 40%, color-mix(in srgb, var(--c) 45%, transparent) 90%);
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
                  mask-composite: exclude;
          opacity: .7;
        }
        .crew-tile:hover {
          border-color: var(--c);
          box-shadow: 0 22px 50px -16px color-mix(in srgb, var(--c) 55%, transparent),
                      0 0 0 1px rgba(255,211,77,.35) inset;
        }
        /* Warrior watermark — crossed blades behind the portrait */
        .war-blades {
          position: absolute; top: 40%; left: 50%;
          transform: translate(-50%, -50%) rotate(0deg);
          color: color-mix(in srgb, var(--c) 100%, transparent);
          opacity: .06;
          pointer-events: none;
          transition: opacity .35s ease, transform .8s cubic-bezier(.2,.9,.3,1);
          z-index: 0;
        }
        .crew-tile:hover .war-blades {
          opacity: .16;
          transform: translate(-50%, -50%) rotate(8deg);
        }
        .royal-crown {
          position: absolute; top: 12px; left: 12px;
          display: inline-flex; align-items: center; justify-content: center;
          width: 22px; height: 22px; border-radius: 6px;
          color: #ffd34d;
          background: linear-gradient(160deg, rgba(255,211,77,.18), rgba(255,211,77,.04));
          border: 1px solid rgba(255,211,77,.4);
          box-shadow: 0 0 12px rgba(255,211,77,.25);
          z-index: 2;
        }
        .rank-badge {
          position: absolute; top: 12px; right: 12px;
          display: inline-flex; align-items: center; gap: 5px;
          padding: 4px 9px; border-radius: 999px;
          font-size: 9px; letter-spacing: 0.22em; font-weight: 700;
          color: #ffd34d;
          background: linear-gradient(160deg, rgba(255,211,77,.20), rgba(0,0,0,.55));
          border: 1px solid rgba(255,211,77,.5);
          box-shadow: 0 0 12px rgba(255,211,77,.18), inset 0 0 8px rgba(255,211,77,.06);
          z-index: 2; white-space: nowrap;
        }
        .rank-live {
          width: 5px; height: 5px; border-radius: 9999px; background: var(--eh-green);
          box-shadow: 0 0 8px var(--eh-green);
          animation: blip 1.6s ease-in-out infinite;
        }
        .sheen {
          position: absolute; inset: 0; pointer-events: none; overflow: hidden;
          border-radius: inherit; z-index: 1;
        }
        .sheen::before {
          content: ''; position: absolute; top: 0; left: -60%; width: 40%; height: 100%;
          transform: skewX(-18deg);
          background: linear-gradient(90deg, transparent, rgba(255,255,255,.05) 40%, rgba(255,211,77,.12) 50%, rgba(255,255,255,.05) 60%, transparent);
          transition: left .7s cubic-bezier(.22,.9,.3,1);
        }
        .crew-tile:hover .sheen::before { left: 130%; }

        /* War-cry chevron strip — hidden by default, slides in on hover */
        .war-cry {
          position: absolute; left: 0; right: 0; bottom: 12px;
          display: flex; justify-content: center; align-items: center; gap: 6px;
          font-size: 8.5px; letter-spacing: 0.35em; font-weight: 700;
          color: color-mix(in srgb, var(--c) 100%, transparent);
          opacity: 0; transform: translateY(6px);
          transition: opacity .3s ease, transform .35s ease;
          text-shadow: 0 0 8px color-mix(in srgb, var(--c) 65%, transparent);
          z-index: 3; pointer-events: none;
        }
        .crew-tile:hover .war-cry { opacity: .95; transform: translateY(0); }
        .war-cry .chev {
          display: inline-block; opacity: .6;
          animation: warChev 1.4s ease-in-out infinite;
        }
        .war-cry .chev:nth-child(2) { animation-delay: .15s; }
        .war-cry .chev:nth-child(3) { animation-delay: .30s; }
        .war-cry .chev:nth-child(5) { animation-delay: .30s; }
        .war-cry .chev:nth-child(6) { animation-delay: .15s; }
        .war-cry .chev:nth-child(7) { animation-delay: 0s; }
        @keyframes warChev {
          0%, 100% { opacity: .35; }
          50%      { opacity: 1; }
        }
        .prism-bar {
          position: absolute; top: 0; left: 0; right: 0; height: 2px;
          background: linear-gradient(90deg, transparent, #ffd34d 20%, var(--c) 50%, #ffd34d 80%, transparent);
          opacity: .85;
        }
        .corner-glow {
          position: absolute; top: -40px; right: -40px; width: 160px; height: 160px;
          background: radial-gradient(circle at center, color-mix(in srgb, var(--c) 40%, transparent), transparent 70%);
          opacity: 0; transition: opacity .35s ease;
        }
        .crew-tile:hover .corner-glow { opacity: 1; }

        /* Portrait frame (photo or premium monogram) */
        .crew-portrait {
          position: relative;
          width: 118px; height: 118px;
          margin: 6px auto 18px;
          border-radius: 9999px;
          overflow: hidden;
          background:
            radial-gradient(circle at 30% 25%, color-mix(in srgb, var(--c) 22%, transparent), transparent 60%),
            #0d1115;
          border: 1.5px solid color-mix(in srgb, var(--c) 55%, transparent);
          box-shadow:
            inset 0 0 32px color-mix(in srgb, var(--c) 22%, transparent),
            0 8px 28px -10px color-mix(in srgb, var(--c) 55%, transparent),
            0 0 0 3px rgba(255,211,77,.12);
          transition: transform .3s ease, box-shadow .3s ease;
          animation: heartbeat 2.4s ease-in-out infinite;
          z-index: 1;
        }
        @keyframes heartbeat {
          0%, 100% { transform: scale(1); }
          14%      { transform: scale(1.028); }
          28%      { transform: scale(1); }
          42%      { transform: scale(1.018); }
          56%      { transform: scale(1); }
        }
        @media (max-width: 480px) {
          .crew-portrait { width: 108px; height: 108px; }
        }
        .crew-tile:hover .crew-portrait {
          animation-play-state: paused;
          transform: translateY(-3px) scale(1.05);
          box-shadow:
            inset 0 0 32px color-mix(in srgb, var(--c) 30%, transparent),
            0 14px 40px -12px color-mix(in srgb, var(--c) 75%, transparent),
            0 0 0 3px rgba(255,211,77,.28);
        }
        .portrait-ring {
          position: absolute; inset: -6px; border-radius: 9999px;
          border: 1px dashed rgba(255,211,77,.3);
          pointer-events: none;
          animation: portraitSpin 22s linear infinite;
        }
        .portrait-ring-outer {
          position: absolute; inset: -14px; border-radius: 9999px;
          background:
            conic-gradient(from 0deg, transparent 0%, rgba(255,211,77,.65) 10%, transparent 25%, transparent 55%, color-mix(in srgb, var(--c) 65%, transparent) 60%, transparent 78%, transparent 100%);
          mask: radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px));
          -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 2px));
          opacity: .55;
          animation: portraitSpin 14s linear infinite reverse;
          pointer-events: none;
        }
        .verified-check {
          position: absolute; right: -2px; bottom: -2px;
          width: 24px; height: 24px; border-radius: 9999px;
          display: grid; place-items: center;
          color: #fff;
          background: radial-gradient(circle at 30% 30%, #4de0ff, #06a3d6);
          box-shadow: 0 0 0 2px #05060a, 0 4px 12px -2px rgba(77,224,255,.7);
          z-index: 3;
        }
        @keyframes portraitSpin { to { transform: rotate(360deg); } }
        .portrait-corner {
          position: absolute; width: 12px; height: 12px;
          border: 1.5px solid #ffd34d;
          opacity: .55; pointer-events: none;
          transition: opacity .3s ease;
        }
        .crew-tile:hover .portrait-corner { opacity: 1; }
        .portrait-corner.tl { top: -3px; left: -3px; border-right: none; border-bottom: none; border-top-left-radius: 4px; }
        .portrait-corner.tr { top: -3px; right: -3px; border-left: none; border-bottom: none; border-top-right-radius: 4px; }
        .portrait-corner.bl { bottom: -3px; left: -3px; border-right: none; border-top: none; border-bottom-left-radius: 4px; }
        .portrait-corner.br { bottom: -3px; right: -3px; border-left: none; border-top: none; border-bottom-right-radius: 4px; }

        .crew-photo {
          width: 100%; height: 100%; object-fit: cover;
          filter: contrast(1.05) saturate(1.05);
        }
        .crew-monogram {
          width: 100%; height: 100%;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          gap: 4px; color: var(--c);
        }
        .crew-initials {
          font-family: 'Cinzel', 'Playfair Display', serif;
          font-weight: 800; font-size: 22px; letter-spacing: 0.12em;
          background: linear-gradient(180deg, var(--c), rgba(255,211,77,.9));
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
        }

        .crew-callsign {
          font-family: 'JetBrains Mono', monospace; font-weight: 800; font-size: 13px;
          letter-spacing: 0.24em; color: var(--c);
        }
        .crew-role {
          font-family: 'Cinzel', 'Playfair Display', serif;
          font-weight: 700; font-size: 20px;
          margin-top: 6px;
          background: linear-gradient(180deg, #ffffff 0%, #d5d5d5 60%, #8a8a8a 100%);
          -webkit-background-clip: text; background-clip: text; -webkit-text-fill-color: transparent;
        }
        .crew-tag {
          font-family: 'Inter', sans-serif; font-size: 12.5px; opacity: .72;
          margin-top: 8px; line-height: 1.55;
          max-width: 240px; margin-left: auto; margin-right: auto;
        }
        .crew-status {
          position: absolute; top: 14px; right: 14px;
          display: inline-flex; align-items: center; gap: 6px;
        }
        .status-live {
          width: 6px; height: 6px; border-radius: 9999px; background: var(--eh-green);
          box-shadow: 0 0 8px var(--eh-green);
          animation: blip 1.6s ease-in-out infinite;
        }

        /* SKILLS MARQUEE */
        .skills-strip {
          position: relative; overflow: hidden;
          border-top: 1px solid var(--eh-border);
          border-bottom: 1px solid var(--eh-border);
          background: rgba(0,255,157,.02);
          padding: 18px 0;
        }
        .skills-strip::before, .skills-strip::after {
          content: ''; position: absolute; top: 0; bottom: 0; width: 80px; z-index: 2; pointer-events: none;
        }
        .skills-strip::before { left: 0; background: linear-gradient(90deg, var(--eh-bg, #05060a), transparent); }
        .skills-strip::after  { right: 0; background: linear-gradient(-90deg, var(--eh-bg, #05060a), transparent); }
        .skills-track {
          display: flex; gap: 14px; width: max-content;
          animation: marquee 36s linear infinite;
        }
        @keyframes marquee { to { transform: translateX(-50%); } }
        .skill-pill {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 6px 14px; border-radius: 999px;
          border: 1px solid var(--eh-border);
          font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.1em;
          color: rgba(255,255,255,.75);
          white-space: nowrap;
        }
        .skill-pill .dot {
          width: 4px; height: 4px; border-radius: 9999px; background: var(--eh-green);
          box-shadow: 0 0 6px var(--eh-green);
        }

        /* CONTACT CARD */
        .contact-card {
          position: relative; padding: 40px 28px;
          border-radius: 20px; border: 1px solid rgba(0,255,157,.28);
          background: linear-gradient(160deg, rgba(0,255,157,.06), rgba(77,224,255,.03) 60%, transparent);
          overflow: hidden;
        }
        .contact-card .prism-bar {
          background: linear-gradient(90deg, transparent, #00ff9d, #4de0ff, #ff2d92, transparent);
          opacity: 1; height: 2px;
        }

        /* Reduce motion */
        @media (prefers-reduced-motion: reduce) {
          .blob, .ring-pulse, .ring-arc, .status-dot, .owner-name .letter, .role-text,
          .status-live, .skills-track, .portrait-ring, .portrait-ring-outer, .sheen::before,
          .crew-portrait, .war-cry .chev, .war-blades { animation: none !important; }
          .owner-name .letter { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
};

export default TeamPage;

// ============================================================================
// <CrewTile /> — SUPER PREMIUM member card
// - Circular portrait with gold ornament + rotating dashed medal ring
// - Verified BadgeCheck overlay (when a photo is uploaded)
// - Rank badge (OP·02, OP·03, …) in the top-right
// - Graceful monogram fallback if the image fails to load
// - Sheen sweep animation on hover
// ============================================================================
const CrewTile = ({ m, rank, tileRef, resolveAsset, Icon }) => {
  const initialSrc = resolveAsset(m.avatar_url);
  const [photoOk, setPhotoOk] = useState(Boolean(initialSrc));
  const initials = useMemo(
    () => (m.callsign || '').split('-').map(s => s[0]).join('').slice(0, 2).toUpperCase(),
    [m.callsign]
  );
  const testKey = m.id || (m.callsign || '').toLowerCase();
  const rankLabel = `OP·${String(rank).padStart(2, '0')}`;

  return (
    <div
      ref={tileRef}
      className="crew-tile crew-royal"
      style={{ '--c': m.color }}
      data-testid={`team-member-${(m.callsign || '').toLowerCase()}`}
    >
      {/* Watermark blades — decorative war emblem */}
      <span className="war-blades" aria-hidden><Swords size={78} strokeWidth={1.1} /></span>
      <span className="royal-crown" aria-hidden><Crown size={11} /></span>
      <span className="rank-badge eh-mono" data-testid={`team-member-rank-${testKey}`}>
        <span className="rank-live" aria-hidden />
        {rankLabel}
      </span>
      <span className="prism-bar" aria-hidden />
      <span className="sheen" aria-hidden />
      <span className="corner-glow" aria-hidden />

      {/* Ornate portrait frame */}
      <div className="crew-portrait" data-testid={`team-member-portrait-${testKey}`}>
        <span className="portrait-ring" aria-hidden />
        <span className="portrait-ring-outer" aria-hidden />
        <span className="portrait-corner tl" aria-hidden />
        <span className="portrait-corner tr" aria-hidden />
        <span className="portrait-corner bl" aria-hidden />
        <span className="portrait-corner br" aria-hidden />
        {photoOk && initialSrc ? (
          <img
            src={initialSrc}
            alt={m.callsign}
            className="crew-photo"
            data-testid={`team-member-photo-${testKey}`}
            onError={() => setPhotoOk(false)}
            loading="lazy"
          />
        ) : (
          <div className="crew-monogram" aria-label="No photo">
            <Icon size={24} strokeWidth={1.6} />
            <span className="crew-initials">{initials}</span>
          </div>
        )}
        {photoOk && initialSrc && (
          <span className="verified-check" title="Verified operative" aria-hidden>
            <BadgeCheck size={13} strokeWidth={2.4} />
          </span>
        )}
      </div>

      <div className="crew-callsign">{m.callsign}</div>
      <div className="crew-role">{m.role}</div>
      <div className="crew-tag">{m.tagline}</div>

      {/* War-cry chevron strip — appears on hover */}
      <div className="war-cry eh-mono" aria-hidden>
        <span className="chev">›</span>
        <span className="chev">›</span>
        <span className="chev">›</span>
        <span>COMBAT READY</span>
        <span className="chev">‹</span>
        <span className="chev">‹</span>
        <span className="chev">‹</span>
      </div>
    </div>
  );
};
